/**
 * Edit Profile Dialog Component
 * Allows users to update their profile information (firstName, lastName, phone, avatar)
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress,
  Typography,
  Avatar,
  Box,
} from '@mui/material';
import { AppButton, AppTextField } from '../design-system';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PhoneField } from '../forms';
import { PhotoCamera, DeleteOutline } from '@mui/icons-material';
import { authService } from '../../services/AuthService';
import {
  EditOwnProfileFormSchema,
  type EditOwnProfileForm,
} from '../../../../shared/src/schemas';
import { personNameInitials } from '@cnapse/shared/person-name.utils.js';
import { TRACKED_UPLOAD_IMAGE_ACCEPT, isTrackedUploadAllowedImageFile } from '@cnapse/shared/file-upload.constants.js';
import type { z } from 'zod';

type EditOwnProfileFormInput = z.input<typeof EditOwnProfileFormSchema>;

interface EditProfileDialogProps {
  open: boolean;
  onClose: () => void;
  currentUser: {
    firstName: string;
    lastName: string;
    phone?: string;
    avatarUrl?: string;
  };
  onSuccess: (updatedUser: {
    firstName: string;
    lastName: string;
    phone?: string;
    avatarUrl?: string;
  }) => void;
  onAvatarChange?: (avatarUrl?: string) => void;
}

export const EditProfileDialog: React.FC<EditProfileDialogProps> = ({
  open,
  onClose,
  currentUser,
  onSuccess,
  onAvatarChange,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(currentUser.avatarUrl);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm<EditOwnProfileFormInput>({
    resolver: zodResolver(EditOwnProfileFormSchema),
    defaultValues: {
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      phone: currentUser.phone ?? '',
    },
  });

  const watchedFirstName = watch('firstName');
  const watchedLastName = watch('lastName');

  useEffect(() => {
    if (open) {
      reset({
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        phone: currentUser.phone ?? '',
      });
      setAvatarUrl(currentUser.avatarUrl);
      setError(null);
    }
  }, [open, currentUser, reset]);

  const handleClose = () => {
    if (!isSubmitting && !avatarUploading) {
      reset();
      setError(null);
      onClose();
    }
  };

  const handleAvatarSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    if (!isTrackedUploadAllowedImageFile(file)) {
      setError('Invalid image type. Allowed: JPEG, PNG, GIF, WebP, BMP, TIFF');
      return;
    }

    setError(null);
    setAvatarUploading(true);
    try {
      const result = await authService.uploadOwnAvatar(file);
      setAvatarUrl(result.data.avatarUrl);
      onAvatarChange?.(result.data.avatarUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    setError(null);
    setAvatarUploading(true);
    try {
      await authService.deleteOwnAvatar();
      setAvatarUrl(undefined);
      onAvatarChange?.(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove avatar');
    } finally {
      setAvatarUploading(false);
    }
  };

  const onSubmit = async (data: EditOwnProfileFormInput) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const validated = EditOwnProfileFormSchema.parse(data);
      const updates: EditOwnProfileForm = validated;

      const response = await authService.updateOwnProfile({
        firstName: updates.firstName,
        lastName: updates.lastName,
        phone: updates.phone ?? undefined,
      });

      reset(data);
      onSuccess({
        firstName: response.data.user.firstName,
        lastName: response.data.user.lastName,
        phone: response.data.user.phone || undefined,
        avatarUrl: response.data.user.avatarUrl ?? avatarUrl,
      });
      handleClose();
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Profile</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={avatarUrl}
                sx={(theme) => ({
                  width: 96,
                  height: 96,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: theme.typography.h5.fontSize,
                  fontWeight: 600,
                })}
              >
                {personNameInitials(watchedFirstName, watchedLastName)}
              </Avatar>
              <Stack direction="row" spacing={1}>
                <AppButton
                  variant="outlined"
                  size="small"
                  startIcon={avatarUploading ? <CircularProgress size={16} /> : <PhotoCamera />}
                  disabled={isSubmitting || avatarUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {avatarUploading ? 'Uploading...' : 'Change Photo'}
                </AppButton>
                {avatarUrl ? (
                  <AppButton
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteOutline />}
                    disabled={isSubmitting || avatarUploading}
                    onClick={handleAvatarRemove}
                  >
                    Remove
                  </AppButton>
                ) : null}
              </Stack>
              <input
                ref={fileInputRef}
                type="file"
                accept={TRACKED_UPLOAD_IMAGE_ACCEPT}
                hidden
                onChange={handleAvatarSelect}
              />
              <Typography variant="caption" color="text.secondary">
                JPEG, PNG, GIF, WebP up to 5 MB
              </Typography>
            </Box>

            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <AppTextField
                  {...field}
                  label="First Name"
                  fullWidth
                  required
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  disabled={isSubmitting}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <AppTextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  required
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  disabled={isSubmitting}
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <PhoneField
                  value={typeof field.value === 'string' ? field.value : field.value ?? null}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  placeholder="+15305512920"
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    'Optional. Use E.164 format (e.g., +15305512920).'
                  }
                  disabled={isSubmitting}
                />
              )}
            />

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                Phone number changes will be synced to your AWS Cognito account.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <AppButton onClick={handleClose} disabled={isSubmitting || avatarUploading}>
            Cancel
          </AppButton>
          <AppButton
            type="submit"
            variant="contained"
            disabled={isSubmitting || avatarUploading || !isDirty}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </AppButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
